import React from "react";
import { WorkflowExecution } from "@/types/workflow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Loader2 } from "lucide-react";
import { ExecutionStatusBadge } from "./ExecutionStatusBadge";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";

interface ActiveExecutionsCardProps {
  executions: WorkflowExecution[];
  isLoading: boolean;
}

export const ActiveExecutionsCard: React.FC<ActiveExecutionsCardProps> = ({
  executions,
  isLoading,
}) => {
  const runningCount = executions.filter(e => e.status === "running").length;
  const pendingCount = executions.filter(e => e.status === "pending").length;
  const waitingCount = executions.filter(e => e.status === "waiting").length;

  return (
    <Card className={`border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-background ${runningCount > 0 ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-2 bg-blue-500 rounded-lg relative ${runningCount > 0 ? 'animate-pulse' : ''}`}>
              <Activity className="h-4 w-4 text-white" />
              {runningCount > 0 && (
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white animate-ping" />
              )}
            </div>
            <span className="text-lg">Actieve Workflows</span>
            {runningCount > 0 && (
              <span className="text-sm font-normal text-blue-600 dark:text-blue-400">
                • Live
              </span>
            )}
          </div>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {executions.length === 0 ? (
          <div className="text-center py-6">
            <Activity className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              Geen actieve workflows
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Start een workflow om realtime updates te zien
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary badges */}
            <div className="flex gap-2 flex-wrap">
              {runningCount > 0 && (
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  {runningCount} actief
                </Badge>
              )}
              {pendingCount > 0 && (
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                  {pendingCount} wachtend
                </Badge>
              )}
              {waitingCount > 0 && (
                <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
                  {waitingCount} wacht op input
                </Badge>
              )}
            </div>

            {/* Active executions list */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {executions.slice(0, 5).map((execution) => (
                <div
                  key={execution.id}
                  className="flex items-center justify-between p-2 bg-white dark:bg-background rounded-lg border"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">
                      {execution.workflow?.name || "Onbekende workflow"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Gestart {formatDistanceToNow(new Date(execution.started_at), {
                        addSuffix: true,
                        locale: nl,
                      })}
                    </p>
                  </div>
                  <ExecutionStatusBadge status={execution.status} size="sm" />
                </div>
              ))}
              {executions.length > 5 && (
                <p className="text-xs text-muted-foreground text-center py-1">
                  +{executions.length - 5} meer
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
